import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Box, Typography } from "@mui/material";
import { Tooltip } from "@shared/Tooltip";

const useStyles = makeStyles()((theme) => ({
    icon: {
        color: theme.palette.emteria.border.primary,
        marginTop: theme.spacing(3.5),
    },
    scope: {
        backgroundColor: theme.palette.action.selected,
        borderRadius: theme.spacing(0.5),
        padding: theme.spacing(0.25, 0.75),
    },
}));

type PermissionGroupProps = {
    heading: string;
    scopes: string[];
};

const PermissionGroup = ({ heading, scopes }: PermissionGroupProps) => {
    const { classes } = useStyles();

    return (
        <Box>
            <Typography variant="caption" component="div" fontWeight={600}>
                {heading}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, marginTop: 0.5 }}>
                {scopes.map((scope) => (
                    <Typography key={scope} component="code" variant="caption" className={classes.scope}>
                        {scope}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};

export const PermissionsHelpTooltip = () => {
    const { classes } = useStyles();
    const { t } = useTranslation("pages");

    return (
        <Tooltip
            title={
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, padding: 0.5 }}>
                    <Typography variant="subtitle2">{t("adminOauthClientsPage.permissionsHelp.title")}</Typography>
                    <PermissionGroup
                        heading={t("adminOauthClientsPage.permissionsHelp.spa")}
                        scopes={[
                            "ept:authorization",
                            "ept:token",
                            "gt:authorization_code",
                            "gt:refresh_token",
                            "scp:offline_access",
                            "scp:<scope>",
                        ]}
                    />
                    <PermissionGroup
                        heading={t("adminOauthClientsPage.permissionsHelp.spaNoRefreshToken")}
                        scopes={["ept:authorization", "ept:token", "gt:authorization_code", "scp:<scope>"]}
                    />
                    <PermissionGroup
                        heading={t("adminOauthClientsPage.permissionsHelp.microservice")}
                        scopes={["ept:token", "gt:client_credentials", "scp:<scope>"]}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {t("adminOauthClientsPage.permissionsHelp.scopePerScope")}
                    </Typography>
                </Box>
            }
        >
            <i
                className={clsx("fa-solid fa-circle-info", classes.icon)}
                role="img"
                aria-label={t("adminOauthClientsPage.permissionsHelp.title")}
                tabIndex={0}
            />
        </Tooltip>
    );
};
